import { useState } from 'react'
import { Skeleton } from '@mui/material'
import { useTranslation } from 'react-i18next'

import { EAccountSubscriptionPlans } from 'src/types'
import { useAccount, useAccountSubscriptionPrices } from 'src/hooks'

import { BillingPlan } from './billing-plan-item.component'
import { BillingModal } from './billing-modal'

import { useStyles } from './styles'
import { CustomBillingModal } from './custom-modal'
import { useAccountSubscription } from 'src/hooks/subscriptions/use-account-subscription.hook'
import { useAccountSubscriptionPlanInfo } from 'src/hooks/payments/use-account-subscription-plan-info.hook'

const BillingPlans = () => {
  const [t] = useTranslation()
  const classes = useStyles()
  const { data: prices } = useAccountSubscriptionPrices()
  const [billingOpen, setBillingOpen] = useState(false)
  const [customOpen, setCustomOpen] = useState(false)
  const { data: account } = useAccount()
  const [subscriptionPlan, setSubscriptionPlan] = useState<EAccountSubscriptionPlans | null>(null)
  const { data: sub } = useAccountSubscription(account?.id ?? '')
  const { data: subscriptionPlanInfo } = useAccountSubscriptionPlanInfo()

  const handleBillingOpen = (subscriptionPlan: EAccountSubscriptionPlans | null) => () => {
    setSubscriptionPlan(subscriptionPlan)
    setBillingOpen(true)
  }

  const handleBillingClose = () => {
    setSubscriptionPlan(null)
    setBillingOpen(false)
  }

  const handleCustomOpen = () => {
    setCustomOpen(true)
  }
  const handleCustomClose = () => {
    setCustomOpen(false)
  }

  return (
    <>
      <div className={classes.plansWrapper}>
        {!account &&
          [1, 2, 3, 4].map((v) => (
            <Skeleton
              key={v}
              width={234}
              height={403}
              variant="rectangular"
              sx={{ borderRadius: '15px' }}
            />
          ))}
        {!!account && (
          <>
            <BillingPlan
              value={0}
              doNotShow={account?.subscription_plan === EAccountSubscriptionPlans.enterprise}
              title="Free"
              monthlyFee="$0"
              yearlyFee="$0"
              description={['1 product', '6 month trial']}
              onButtonClick={handleBillingOpen(null)}
              buttonDisabled={
                !(
                  subscriptionPlanInfo?.expiered_plan === EAccountSubscriptionPlans.free &&
                  subscriptionPlanInfo?.expired
                )
              }
              buttonText={
                account.subscription_plan === EAccountSubscriptionPlans.free && !!sub
                  ? t('billing-current-plan.v')
                  : subscriptionPlanInfo?.expired &&
                      subscriptionPlanInfo?.expiered_plan === EAccountSubscriptionPlans.free
                    ? t('upgrade-button-text.v')
                    : t('get-started-button-text.v')
              }
              isActivated={account.subscription_plan === EAccountSubscriptionPlans.free && !!sub}
              showExpired={
                (subscriptionPlanInfo?.expired &&
                  subscriptionPlanInfo?.expiered_plan === EAccountSubscriptionPlans.free) ??
                false
              }
            />
            <BillingPlan
              value={1}
              doNotShow={account?.subscription_plan === EAccountSubscriptionPlans.enterprise}
              popularChoice
              title="Plus"
              monthlyFee={prices?.plusMonthly || null}
              yearlyFee={prices?.plusYearly || null}
              yearlyCalculation={prices?.plusYearlyCalculation}
              discount={prices?.plusYearlyDiscount}
              description={[
                'Up to 5 products',
                'Multi-lingual interface',
                'Rich text formatting',
                'Extra media layout options',
              ]}
              onButtonClick={handleBillingOpen(EAccountSubscriptionPlans.plus)}
              buttonDisabled={
                account.subscription_plan === EAccountSubscriptionPlans.plus ||
                account.subscription_plan === EAccountSubscriptionPlans.premium
              }
              buttonText={
                account.subscription_plan === EAccountSubscriptionPlans.plus
                  ? t('billing-current-plan.v')
                  : t('get-started-button-text.v')
              }
              isActivated={account.subscription_plan === EAccountSubscriptionPlans.plus}
              showExpired={
                (subscriptionPlanInfo?.expired &&
                  subscriptionPlanInfo?.expiered_plan === EAccountSubscriptionPlans.plus) ??
                false
              }
            />
            <BillingPlan
              value={2}
              doNotShow={account?.subscription_plan === EAccountSubscriptionPlans.enterprise}
              title="Premium"
              monthlyFee={prices?.premiumMonthly || null}
              yearlyFee={prices?.premiumYearly || null}
              yearlyCalculation={prices?.premiumYearlyCalculation}
              discount={prices?.premiumYearlyDiscount}
              description={['Same as the Plus Plan', 'Up to 25 products']}
              onButtonClick={handleBillingOpen(EAccountSubscriptionPlans.premium)}
              buttonDisabled={account.subscription_plan === EAccountSubscriptionPlans.premium}
              buttonText={
                account.subscription_plan === EAccountSubscriptionPlans.premium
                  ? t('billing-current-plan.v')
                  : account.subscription_plan === EAccountSubscriptionPlans.plus
                    ? t('upgrade-button-text.v')
                    : t('get-started-button-text.v')
              }
              isActivated={account.subscription_plan === EAccountSubscriptionPlans.premium}
              showExpired={
                (subscriptionPlanInfo?.expired &&
                  subscriptionPlanInfo?.expiered_plan === EAccountSubscriptionPlans.premium) ??
                false
              }
            />
            <BillingPlan
              value={3}
              title="Enterprise"
              description={[
                'Same as the Premium Plan',
                'Dedicated account manager',
                'Implementation project management',
                'Custom integrations and development',
                'API access',
              ]}
              onButtonClick={handleCustomOpen}
              buttonText={
                account.subscription_plan === EAccountSubscriptionPlans.enterprise
                  ? t('billing-current-plan.v')
                  : t('subscription-custom-button-text.v')
              }
              buttonDisabled={account.subscription_plan === EAccountSubscriptionPlans.enterprise}
              isActivated={account.subscription_plan === EAccountSubscriptionPlans.enterprise}
              showExpired={false}
            />
          </>
        )}
      </div>
      <CustomBillingModal open={customOpen} onClose={handleCustomClose} />
      <BillingModal
        onClose={handleBillingClose}
        open={billingOpen}
        subscription_plan={subscriptionPlan}
      />
    </>
  )
}

export { BillingPlans }
